import { z } from 'zod';

import { StringNumberSchema } from '../const/stringNumber';
import { EnvironmentIdSchema, NewTagIdSchema } from '../id';

import { ApiCountrySchema } from './countries';
import { Dimensions } from './dimensions';

export const DataExportDimensionIdSchema = z.string().brand('DataExportDimensionId');
export type DataExportDimensionId = z.infer<typeof DataExportDimensionIdSchema>;

export const DataExportMetricIdSchema = z.string().brand('DataExportMetricId');
export type DataExportMetricId = z.infer<typeof DataExportMetricIdSchema>;

/*
 * =============
 * = UI Models =
 * =============
 */

export const ApiCreateDimensionPropertyPathSchema = z.enum([
  'dimensions',
  'tags.variableConfigs',
  'tags.bestPracticeConfigs',
]);

const ApiDimensionTypesSchema = ApiCreateDimensionPropertyPathSchema.extract(['dimensions', 'tags.variableConfigs']);

export type ApiDimensionTypes = z.infer<typeof ApiDimensionTypesSchema>;

export type ApiCreateDimensionPropertyPath = z.infer<typeof ApiCreateDimensionPropertyPathSchema>;

// Common fields for UI interop (not anything data wise)
export const ApiCreateCommonDimensionPropertiesSchema = z.object({
  id: DataExportDimensionIdSchema,
  friendlyName: z.string(),
  originalFriendlyName: z.string().optional(),
  source: ApiCreateDimensionPropertyPathSchema,
  sourceGroup: z.string().optional(),
  group: z.string().optional(),
  type: z.string().optional(),
  meta: z.any().optional(),
  allowedInSegmentation: z.boolean().optional(),
  allowedInDimensions: z.boolean().optional(),
  allowedInBestPracticeCustomization: z.boolean().optional(),
  allowedInQualifiers: z.boolean().optional(),
  allowedInConsentCategory: z.boolean().optional(),
  allowedInConsentTagMapping: z.boolean().optional(),
  manualEntryPrefix: z.string().nullable().optional(),
  manualEntrySource: ApiDimensionTypesSchema.nullable().optional(),
  manualEntryAllowedType: z.string().nullable().optional(),
  uiFlags: z
    .object({
      showInConsentCategory: z.boolean().optional(),
      showInConsentTagMapping: z.boolean().optional(),
      showInSegments: z.boolean().optional(),
      showInDimensions: z.boolean().optional(),
      showInBestPracticeCustomization: z.boolean().optional(),
      showInQualifiers: z.boolean().optional(),
    })
    .catchall(z.any())
    .optional(),
});

export type ApiCreateCommonDimensionProperties = z.infer<typeof ApiCreateCommonDimensionPropertiesSchema>;

// Main id is dimensionId,
// UI also adds in an id and friendlyName field but that is only to render in the UI
export const ApiCreateStandardDimensionSchema = ApiCreateCommonDimensionPropertiesSchema.extend({
  eventType: z.string().optional(),
  dimensionId: DataExportDimensionIdSchema,
  source: ApiCreateDimensionPropertyPathSchema.extract(['dimensions']),
});

export type ApiCreateStandardDimension = z.infer<typeof ApiCreateStandardDimensionSchema>;

// See above notes
export const ApiCreateTagVariableDimensionSchema = ApiCreateCommonDimensionPropertiesSchema.extend({
  tagId: z.string(),
  tagFriendlyName: z.string(),
  dimensionId: z.string().optional(),
  variableId: DataExportDimensionIdSchema,
  variablePath: z.string().optional(),
  category: z.string(),
  source: ApiCreateDimensionPropertyPathSchema.extract(['tags.variableConfigs']),
  eventDimension: ApiCreateStandardDimensionSchema.optional(),
});

export type ApiCreateTagVariableDimension = z.infer<typeof ApiCreateTagVariableDimensionSchema>;

export const ApiCreateTagBestPracticeDimensionSchema = ApiCreateCommonDimensionPropertiesSchema.extend({
  source: ApiCreateDimensionPropertyPathSchema.extract(['tags.bestPracticeConfigs']),
  tagId: z.string(),
  lzStringCompressedId: z.string(),
  commonBestPracticeId: z.string(),
});

export type ApiCreateTagBestPracticeDimension = z.infer<typeof ApiCreateTagBestPracticeDimensionSchema>;

export const ApiCreateDimensionSchema = z.union([
  ApiCreateStandardDimensionSchema,
  ApiCreateTagVariableDimensionSchema,
]);

export type ApiCreateDimension = z.infer<typeof ApiCreateDimensionSchema>;

/*
 * ==============
 * = API Models =
 * ==============
 */

export const ApiDataExportStandardDimensionSchema = z
  .object({
    id: DataExportDimensionIdSchema,
    friendlyName: z.string(),
    type: z.string().optional(),
    column: z.string().optional(),
    eventType: z.string().optional(),
    uiGrouping: z.string().optional(),
    propertySourceCategory: z.string().optional(),
    uiFlags: z
      .object({
        showInConsentCategory: z.boolean().optional(),
        showInConsentTagMapping: z.boolean().optional(),
        showInSegments: z.boolean().optional(),
        showInDimensions: z.boolean().optional(),
        showInBestPracticeCustomization: z.boolean().optional(),
        showInQualifiers: z.boolean().optional(),
        manualEntryPrefix: z.string().optional(),
        manualEntryAllowedType: z.string().optional(),
        manualEntrySource: z.any().transform((val) => {
          try {
            return ApiDimensionTypesSchema.parse(val);
          } catch {
            return null;
          }
        }) as z.ZodType<ApiDimensionTypes | null>,
      })
      .optional(),
  })
  .transform((data) => {
    const output: ApiCreateStandardDimension = {
      id: data.id,
      dimensionId: data.id,
      friendlyName: data.friendlyName,
      source: 'dimensions',
      type: data.type ?? 'Any',
      group: data.uiGrouping ?? 'Dimensions',
      sourceGroup: data.propertySourceCategory,
      uiFlags: {
        ...data.uiFlags,
        showInSegments: data.uiFlags?.showInSegments ?? true,
        showInDimensions: data.uiFlags?.showInDimensions ?? true,
      },
      // TODO: CHANGE THIS TO FALSE
      allowedInSegmentation: data.uiFlags?.showInSegments ?? true,
      allowedInDimensions: data.uiFlags?.showInDimensions ?? true,
      allowedInBestPracticeCustomization: data.uiFlags?.showInBestPracticeCustomization ?? false,
      allowedInQualifiers: data.uiFlags?.showInQualifiers ?? false,
      allowedInConsentCategory: data.uiFlags?.showInConsentCategory ?? false,
      allowedInConsentTagMapping: data.uiFlags?.showInConsentTagMapping ?? false,
      manualEntryPrefix: data.uiFlags?.manualEntryPrefix ?? null,
      manualEntrySource: data.uiFlags?.manualEntrySource ?? null,
      manualEntryAllowedType: data.uiFlags?.manualEntryAllowedType ?? null,
      eventType: data.eventType,
    };

    return output;
  });

export type ApiDataExportDimension = z.infer<typeof ApiDataExportStandardDimensionSchema>;

export const ApiDataExportTagVariableDimensionSchema = z.object({
  _id: NewTagIdSchema,
  friendlyName: z.string(),
  logoUrl: z.string().optional(),
  oldId: z.string().optional(),
  variables: z.array(
    z.object({
      id: DataExportDimensionIdSchema,
      friendlyName: z.string(),
      technicalName: z.string().optional(),
      categoryFriendlyName: z.string().default(''),
      propertyPath: z.string(),
      tagId: z.string().optional(),
    })
  ),
});

export type ApiDataExportTag = z.infer<typeof ApiDataExportTagVariableDimensionSchema>;

export const ApiDataExportMetricSchema = z
  .object({
    id: DataExportMetricIdSchema,
    friendlyName: z.string(),
  })
  .transform((data) => ({
    metricId: data.id,
    friendlyName: data.friendlyName,
  }));

export type ApiDataExportMetric = z.infer<typeof ApiDataExportMetricSchema>;

export const ApiDataExportDimensionsAndMetricsBestPracticesSchema = z.object({
  id: z.string(),
  title: z.string(),
  commonBestPracticeId: z.string(),
});

export const ApiDataExportDimensionsAndMetricsTagIdToBestPracticesMapSchema = z.record(
  z.array(ApiDataExportDimensionsAndMetricsBestPracticesSchema)
);

export const ApiDataExportDimensionsAndMetricsSchema = z
  .object({
    tags: z.array(ApiDataExportTagVariableDimensionSchema),
    metrics: z.array(ApiDataExportMetricSchema),
    dimensions: z.array(ApiDataExportStandardDimensionSchema),
    tagIdToBestPracticesMap: ApiDataExportDimensionsAndMetricsTagIdToBestPracticesMapSchema.default({}),
    countries: z.array(ApiCountrySchema),
  })
  .transform((data) => {
    const flatTags: ApiCreateTagVariableDimension[] = data.tags.flatMap((tag) =>
      tag.variables.map((variable) => ({
        tagId: tag._id,
        variableId: variable.id,
        source: 'tags.variableConfigs',
        type: 'string',
        tagFriendlyName: tag.friendlyName,
        category: variable.categoryFriendlyName,
        friendlyName: `${variable.friendlyName} (${variable.propertyPath})`,
        originalFriendlyName: variable.friendlyName,
        id: `${tag._id}.${variable.id}.${variable.propertyPath}` as DataExportDimensionId,
        variablePath: variable.propertyPath,
        allowedInSegmentation: true,
        allowedInDimensions: true,
      }))
    );

    for (const tag of data.tags) {
      const consentViolationDynamicVariable: ApiCreateTagVariableDimension = {
        tagId: tag._id,
        variablePath: 'full_url',
        source: 'tags.variableConfigs',
        id: `${tag._id}.full_url` as DataExportDimensionId,
        friendlyName: 'Consent Violations',
        tagFriendlyName: tag.friendlyName,
        variableId: 'consent_violation_full_url' as DataExportDimensionId,
        category: 'Consent Violations',
        allowedInSegmentation: true,
      };

      flatTags.push(consentViolationDynamicVariable);
    }

    const finalDimensions = [...data.dimensions, ...flatTags];

    const combinedData: DataExportDimensionsAndMetrics = {
      ...data,
      segmentationDimensions: finalDimensions.map((x) => {
        if (x.source === 'dimensions' && x.eventType) {
          const meta = Dimensions.find((y) => y.id === x.id);
          const group =
            x.group || meta?.group || (x.source === 'dimensions' ? 'Dimensions' : 'Network Request Variables');
          const dimensionAsEvent: ApiCreateTagVariableDimension = {
            id: x.id,
            friendlyName: x.friendlyName,
            category: x.group ?? 'Browser',
            source: 'tags.variableConfigs',
            tagFriendlyName: 'Event',
            tagId: 'event',
            allowedInSegmentation: true,
            variableId: x.dimensionId,
            type: x.type,
            meta,
            group,
            eventDimension: x,
          };

          return dimensionAsEvent;
        }
        const meta =
          x.source === 'tags.variableConfigs' ? { group: x.category } : Dimensions.find((y) => y.id === x.id);
        const group =
          x.group || meta?.group || (x.source === 'dimensions' ? 'Dimensions' : 'Network Request Variables');

        return {
          ...x,
          group,
          meta,
        };
      }),
      dimensions: finalDimensions.map((x) => {
        // if (x.source === 'dimensions' && x.eventType) {
        //   const meta = Dimensions.find((y) => y.id === x.id);
        //   const group = x.group || meta?.group || (x.source === 'dimensions' ? 'Dimensions' : 'Tag Variables');
        //   const dimensionAsEvent: ApiCreateTagVariableDimension = {
        //     id: x.id,
        //     friendlyName: x.friendlyName,
        //     category: x.group ?? 'Browser',
        //     source: 'tags.variableConfigs',
        //     tagFriendlyName: 'Event',
        //     tagId: 'event',
        //     allowedInSegmentation: true,
        //     variableId: x.dimensionId,
        //     type: x.type,
        //     meta,
        //     group,
        //     eventDimension: x,
        //   };

        //   return dimensionAsEvent;
        // }
        const meta =
          x.source === 'tags.variableConfigs' ? { group: x.category } : Dimensions.find((y) => y.id === x.id);
        const group =
          x.group || meta?.group || (x.source === 'dimensions' ? 'Dimensions' : 'Network Request Variables');

        return {
          ...x,
          group,
          meta,
        };
      }),
    };

    return combinedData;
  });

export type ApiDataExportDimensionsAndMetrics = z.infer<typeof ApiDataExportDimensionsAndMetricsSchema>;

export const DataExportDimensionsAndMetricsSchema = z.object({
  metrics: z.array(ApiDataExportMetricSchema),
  dimensions: z.array(ApiCreateDimensionSchema),
  segmentationDimensions: z.array(ApiCreateDimensionSchema),
  tags: z.array(ApiDataExportTagVariableDimensionSchema),
  tagIdToBestPracticesMap: ApiDataExportDimensionsAndMetricsTagIdToBestPracticesMapSchema.default({}),
  countries: z.array(ApiCountrySchema),
  // bestPractices: z.array(ApiDataExportDimensionsAndMetricsBestPracticesSchema).default([]),
});

export type DataExportDimensionsAndMetrics = z.infer<typeof DataExportDimensionsAndMetricsSchema>;

export const ApiDataExportPostfixOperandsSchema = z.object({
  propertyPath: z.union([ApiCreateDimensionSchema, ApiCreateTagBestPracticeDimensionSchema]),
  comparator: z.string(),
  ignoreCase: z.boolean().optional().nullable(),
  comparisonAttribute: z.string(),
  comparisonValue: z.any(),
});

export type ApiDataExportPostfixOperands = z.infer<typeof ApiDataExportPostfixOperandsSchema>;

export const ApiDataExportPostfixSchema = z.object({
  postfixExpression: z.array(z.union([StringNumberSchema, z.literal('&'), z.literal('|')])),
  operands: z.array(ApiDataExportPostfixOperandsSchema),
  filterLevel: z.enum(['pageview', 'event']),
});

export type ApiDataExportPostfix = z.infer<typeof ApiDataExportPostfixSchema>;

// ====================
// = FINAL ============
// ====================

const ApiDataExportPayloadDimensionSchema = z.union([
  z.object({
    dimensionId: DataExportDimensionIdSchema,
    source: ApiCreateDimensionPropertyPathSchema.extract(['dimensions']),
  }),
  z.object({
    tagId: NewTagIdSchema,
    // variableId: DataExportDimensionIdSchema,
    // variablePath: z.string().optional(),
    dataTypes: z
      .array(
        z.enum([
          'request_data',
          'request_metadata',
          'anomaly_data',
          'pii_data',
          'consent_violations',
          'missing_requests',
        ])
      )
      .min(1),
    source: ApiCreateDimensionPropertyPathSchema.extract(['tags.variableConfigs']),
  }),
]);

const ApiDataExportPayloadPropertyPathSchema = z.union([
  z.object({
    dimensionId: DataExportDimensionIdSchema,
    source: ApiCreateDimensionPropertyPathSchema.extract(['dimensions']),
  }),
  z.object({
    lzStringCompressedId: z.string(),
    commonBestPracticeId: z.string(),
    source: ApiCreateDimensionPropertyPathSchema.extract(['tags.bestPracticeConfigs']),
  }),
  z.object({
    tagId: NewTagIdSchema,
    variableId: DataExportDimensionIdSchema,
    variablePath: z.string().optional(),
    // dataTypes: z.array(z.enum(['request_data', 'anomaly_data', 'pii_data', 'consent_violations', 'missing_requests])),
    source: ApiCreateDimensionPropertyPathSchema.extract(['tags.variableConfigs']),
  }),
]);

export type ApiDataExportPayloadDimension = z.infer<typeof ApiDataExportPayloadDimensionSchema>;

export const ApiDataExportSchema = z.object({
  name: z.string().max(200),
  format: z.enum(['csv', 'json']),
  environmentGuid: EnvironmentIdSchema,
  filters: z.array(
    z.object({
      filterLevel: ApiDataExportPostfixSchema.shape.filterLevel,
      postfixExpression: ApiDataExportPostfixSchema.shape.postfixExpression,
      operands: z.array(
        ApiDataExportPostfixOperandsSchema.extend({
          propertyPath: ApiDataExportPayloadPropertyPathSchema,
          comparisonValue: z
            .any()
            .refine((val) => (val instanceof Error ? false : true))
            .refine((val) => (typeof val === 'string' ? val.length > 0 : true), {
              message: 'A string comparison value must not be empty',
            })
            .refine(
              (val) =>
                Array.isArray(val)
                  ? val.length > 0 && val.every((x) => (typeof x === 'string' ? x.length > 0 : true))
                  : true,
              {
                message: 'An array comparison value must not be empty',
              }
            ),
        })
      ),
    })
  ),
  emails: z.array(z.string().email()).optional(),
  limit: z.number().min(1).max(50000).optional().nullable(),
  metrics: z.array(
    z.object({
      metricId: DataExportMetricIdSchema,
    })
  ),
  dimensions: z.array(ApiDataExportPayloadDimensionSchema),
  sorting: z
    .array(
      z.object({
        dimensionId: DataExportDimensionIdSchema,
        order: z.enum(['asc', 'desc']),
      })
    )
    .optional()
    .nullable(),
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
});

export type ApiDataExport = z.infer<typeof ApiDataExportSchema>;

/** Relative window for applying a saved export template (mirrors scheduled export semantics). */
export type DataExportTemplateDateConfig = {
  unit: 'day' | 'week' | 'month';
  value: number;
};

/** Values passed into a template’s `payload` builder (e.g. tags chosen in the UI). */
export type DataExportTemplateInput = {
  selectedTags: string[];
};

/** Hardcoded or saved template: full export fields except env and concrete dates (filled on apply). */
export type DataExportTemplate = {
  id: string;
  title: string;
  description?: string;
  date: DataExportTemplateDateConfig;
  /** When set, the apply flow collects the listed inputs before calling `payload`. */
  inputs?: Partial<Record<keyof DataExportTemplateInput, boolean>>;
  payload: (input: DataExportTemplateInput) => Omit<ApiDataExport, 'environmentGuid' | 'dateRange'>;
};
